import { useEffect, useState } from 'react'
import { Button, ErrorBanner, Modal } from '../../components/forms'
import { HeldStockPanel } from './HeldStockPanel'
import { useBookCollection, useStockElsewhere, type CollectionOrderResult, type PosCustomer } from '../../features/pos/api'

/**
 * The shop is short of something (D-193).
 *
 * Three ways out, in the order a cashier would try them: take what is here, find the rest at another
 * shop, or give up on the line. The dialog exists rather than a banner because the second of those is
 * a decision with consequences — a transfer is raised and a customer is told to come back — and a
 * toast that disappears after five seconds is no place to make it.
 *
 * **It never claims the goods are secured.** Booking asks the other shop; that shop holds the stock
 * when it agrees. Saying "reserved" here would be the one sentence this whole design exists to avoid.
 *
 * **D-198 added a fourth way out, above the other three:** the units may not be missing at all but
 * held — by another counter's basket, or by a sale somebody parked and has not come back for. So the
 * dialogue first says who holds them, and a manager may hand them over before the hold's time is up.
 */
export function ShortageDialog({
  locationId,
  registerSessionId,
  customer,
  shortage,
  onReduce,
  onRemove,
  onReleased,
  onClose,
}: {
  locationId: string
  /** This till's session, so its own basket is not offered back to it. */
  registerSessionId: string
  customer: PosCustomer
  shortage: { productId: string; name: string; requested: number; available: number } | null
  onReduce: (productId: string, quantity: number) => void
  onRemove: (productId: string) => void
  /** A hold was given to this sale, so the basket is worth quoting again. */
  onReleased: () => void
  onClose: () => void
}) {
  const elsewhere = useStockElsewhere(locationId)
  const book = useBookCollection(locationId)

  const [booked, setBooked] = useState<CollectionOrderResult | null>(null)
  const shortBy = shortage ? shortage.requested - shortage.available : 0

  // Asked once, when the dialog opens for a line. Not on every render: a cashier changing their mind
  // twice should not send the question round every branch twice.
  useEffect(() => {
    if (shortage) {
      setBooked(null)
      book.reset()
      elsewhere.mutate({ productId: shortage.productId, quantity: shortBy })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the line, not the callbacks
  }, [shortage?.productId, shortage?.requested])

  if (!shortage) {
    return null
  }

  const places = elsewhere.data ?? []

  return (
    <Modal open title={booked ? 'Ordered in' : `Not enough ${shortage.name}`} onClose={onClose}>
      {booked ? (
        <div className="space-y-3 text-sm">
          <p>
            <strong>{booked.quantity} × {shortage.name}</strong> has been asked for from{' '}
            <strong>{booked.fromLocationName}</strong>.
          </p>

          {/* Said plainly, because it is the difference between a promise and a request. */}
          <p className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2">
            {booked.fromLocationName} will set it aside once they accept the request. Tell the customer
            we will ring them — not that it is already put by.
          </p>

          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-dpl-muted">Order</dt>
            <dd className="font-medium tabular-nums">{booked.orderNumber}</dd>
            <dt className="text-dpl-muted">Stock request</dt>
            <dd className="font-medium tabular-nums">{booked.indentCode}</dd>
          </dl>

          <p className="text-dpl-muted">Nothing has been charged. They pay when they collect.</p>

          <Button onClick={onClose}>Done</Button>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p>
            {shortage.available <= 0
              ? `There is none of this in the shop.`
              : `Only ${shortage.available} in the shop, and the sale asks for ${shortage.requested}.`}
          </p>

          <ErrorBanner error={book.error ?? elsewhere.error} />

          {/* First, because it is the cheapest answer: the stock may be in the shop already. */}
          <HeldStockPanel
            locationId={locationId}
            registerSessionId={registerSessionId}
            productId={shortage.productId}
            onReleased={onReleased}
          />

          <div className="flex flex-wrap gap-2">
            {shortage.available > 0 && (
              <Button
                tone="secondary"
                onClick={() => {
                  onReduce(shortage.productId, shortage.available)
                  onClose()
                }}
              >
                Sell the {shortage.available} that are here
              </Button>
            )}

            <Button
              tone="ghost"
              onClick={() => {
                onRemove(shortage.productId)
                onClose()
              }}
            >
              Take it off the sale
            </Button>
          </div>

          <div>
            <p className="mb-2 font-semibold">Bring {shortBy} in from somewhere else</p>

            {elsewhere.isPending ? (
              <p className="text-dpl-muted">Looking…</p>
            ) : places.length === 0 ? (
              <p className="text-dpl-muted">No other shop has any either.</p>
            ) : customer.isWalkIn ? (
              // Somebody has to be telephoned when it arrives, and the walk-in record is shared by
              // every anonymous sale in the company.
              <p className="rounded-md border border-dpl-border bg-dpl-bg px-3 py-2">
                Take the customer's name and number first — somebody has to ring them when it arrives.
              </p>
            ) : (
              <ul className="space-y-2">
                {places.map((place) => (
                  <li
                    key={place.locationId}
                    className="flex items-center justify-between gap-3 rounded-lg border border-dpl-border px-3 py-2"
                  >
                    <span>
                      <span className="font-medium">{place.name}</span>
                      <span className="block text-xs text-dpl-muted">
                        {place.available} there
                        {place.distanceKm !== undefined && ` · ${place.distanceKm} km`}
                        {!place.enough && ` · not the whole ${shortBy}`}
                      </span>
                    </span>

                    <Button
                      tone="secondary"
                      busy={book.isPending}
                      onClick={async () =>
                        setBooked(await book.mutateAsync({
                          customerId: customer.id,
                          fromLocationId: place.locationId,
                          productId: shortage.productId,
                          quantity: Math.min(shortBy, place.available),
                        }))
                      }
                    >
                      Ask for {Math.min(shortBy, place.available)}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
